import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { LoaderService } from '../../../../core/services/loader.service';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ComparisonType,
  DatePickerComponent,
  DateRangePickerOutput,
  DateRangePickerValue
} from '../../../../shared/components/date-picker/date-picker.component';
import { DonutChartComponent } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { getCalendarEquivalentPreviousPeriod, getComparisonLabel, KpiComparisonLabel } from '../../../../shared/utils/date-utils';
dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

@Component({
  selector: 'app-recuvery-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerComponent,
    IndividualToasterComponent,
    DonutChartComponent,
  ],
  templateUrl: './recuvery-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './recuvery-dashboard.component.scss'
})
export class RecuveryDashboardComponent {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private unitService = inject(UnitSuggestionService);
  private loaderService = inject(LoaderService);
  private fetchSubscription?: Subscription;
  /** Avoid fetching with component defaults before the date picker emits its range. */
  private datesReady = false;
  private fetchRequestId = 0;
  private lastDateRangeKey = '';
  /** Skip one effect-driven fetch when onDateRangeChange already triggered it. */
  private skipNextEffectFetch = false;
  private initialLoadDone = false;
  // Date state
  start_date = '';
  end_date = '';
  prev_start_date = '';
  prev_end_date = '';
  comparisonType: any = 'none';
  selectedPresetLabel = '';
  unit = signal<any>('month');
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];

  // Decision Signals
  decisionSource = signal<any>(null);
  decisionRecovery = signal<any>(null);
  decisionPipeline = signal<any>(null);

  // Recovery Book Status Signals
  debtRecovered = signal<any>(null);
  accountsPaidOff = signal<any>(null);
  activePortfolio = signal<any>(null);
  recoveryRate = signal<any>(null);
  totalContracts = signal<any>(null);

  // Remaining signals for cards
  recoveryJourney = signal<any>(null);
  contractLifecycle = signal<any>(null);
  recoverySource = signal<any>(null);
  platformFee = signal<any>(null);
  channelFunnel = signal<any>(null);

  demandLeter = signal<any>(null);
  sms = signal<any>(null);
  email = signal<any>(null);

  /**
   * Channel Funnel summary paragraph — built entirely from channelFunnel()?.channels, the same
   * signal that renders the table, so the two can never disagree. Recomputes automatically
   * whenever channelFunnel() changes (date-range/comparison changes drive fetchData(), which
   * re-sets channelFunnel()). No channel name, percentage, or count is hardcoded — everything is
   * read off each channel's own record.
   */
  channelFunnelSummary = computed<string | null>(() => {
    const channels: any[] = this.channelFunnel()?.channels || [];
    if (!channels.length) return null;

    // A channel only participates in the payment funnel (Attempted/Paid/end-yield) when the API
    // gave it a rank. rank === null marks a reminder/outreach channel (e.g. calls) whose
    // Attempted/Paid cells the table itself renders as "--" — so it must never be scored on
    // conversion/end-yield, and its reach metric must never be labeled an "open rate".
    const funnelChannels = channels.filter(c => c.rank !== null && c.rank !== undefined);

    // The table's own column header ("Opened / Scanned / Answered") already carries three
    // distinct verbs for three channel shapes — reuse that same distinction here by channel_key
    // rather than inventing new wording, so Calls is described as "answered", never "opened".
    const reachLabel = (channelKey: string): string => {
      const key = (channelKey || '').toLowerCase();
      if (key === 'calls') return 'answered rate';
      if (key === 'demand_letter') return 'scanned rate';
      return 'open rate';
    };

    // Reach: whichever channel has the highest opened/scanned/answered rate, of any type.
    const reachChannel = channels.reduce((a, b) => (Number(b.open_rate_pct) || 0) > (Number(a.open_rate_pct) || 0) ? b : a, channels[0]);
    const reachRate = Number(reachChannel.open_rate_pct) || 0;
    let reachSentence = `${reachChannel.name} reaches the most recipients (${reachRate.toFixed(1)}% ${reachLabel(reachChannel.channel_key)})`;

    // Only compute an opened-but-never-attempted drop-off when this channel is actually part of
    // the payment funnel (never for Calls, or any other non-funnel channel) — and only against
    // the correct funnel-stage denominator (opened), never against Sent.
    const reachIsFunnelChannel = funnelChannels.some(c => c.channel_key === reachChannel.channel_key);
    if (reachIsFunnelChannel) {
      const opened = Number(reachChannel.opened) || 0;
      const attempted = Number(reachChannel.attempted) || 0;
      if (opened > 0) {
        const dropPct = Math.max(0, ((opened - attempted) / opened) * 100);
        reachSentence += `, but ${dropPct.toFixed(1)}% of ${reachChannel.name} recipients never proceed to attempting payment`;
      }
    }
    reachSentence += '.';

    const failing = channels.filter(c => (Number(c.delivery_failures) || 0) > 0);
    const failuresSentence = failing.length
      ? `Delivery failures were reported for ${failing.map(c => `${c.name} (${Number(c.delivery_failures) || 0})`).join(', ')}.`
      : `No delivery failures were reported across the ${channels.length} tracked channel${channels.length === 1 ? '' : 's'}.`;

    if (!funnelChannels.length) {
      return `${reachSentence} ${failuresSentence}`;
    }

    const best = funnelChannels.reduce((a, b) => (Number(b.end_yield_pct) || 0) > (Number(a.end_yield_pct) || 0) ? b : a, funnelChannels[0]);
    const worst = funnelChannels.reduce((a, b) => (Number(b.end_yield_pct) || 0) < (Number(a.end_yield_pct) || 0) ? b : a, funnelChannels[0]);
    const bestYield = Number(best.end_yield_pct) || 0;
    const worstYield = Number(worst.end_yield_pct) || 0;

    const yieldSentence = best.channel_key === worst.channel_key
      ? `${best.name} delivers the best end-to-end yield (${bestYield.toFixed(1)}%) among the payment-funnel channels.`
      : `${best.name} delivers the best end-to-end yield (${bestYield.toFixed(1)}%), while ${worst.name} lags furthest behind (${worstYield.toFixed(1)}%).`;

    return `${yieldSentence} ${reachSentence} ${failuresSentence}`;
  });

  // Contract Lifecycle donut: legend label currently hovered, so the chart can
  // highlight the matching segment.
  hoveredContractLabel = signal<string | null>(null);

  // Debt Recovery progress bar: stage key currently hovered, so the other
  // segments can fade while it's highlighted.
  hoveredDebtStageKey = signal<string | null>(null);



  /**
   * Drives every `@if(isLoading()){ <app-individual-toaster> }` skeleton overlay in this
   * template (same shared loader component/behavior as Monthly Report 1's per-section
   * `isSectionLoading()` overlays). Set true the moment fetchData() starts building a request
   * and false only once the forkJoin response has fully populated every KPI signal below, so
   * the overlay never drops mid-update and no stale/partial value is ever visible underneath.
   */
  isLoading = signal(false);
  contactsData = signal<{ active: number; overdue: number; compeleted: number; cancelled: number; total: number }>({
    active: 55.4,
    overdue: 8,
    compeleted: 31.7,
    cancelled: 4.9,
    total: 7581
  });
  constructor() {
    effect(() => {
      // Must read the signal synchronously here so Angular registers the dependency
      const loading = this.isLoading();
      // Wait until dates are populated (first fetch initiated)
      if (!this.start_date || !this.end_date) return;

      if (!loading && !this.initialLoadDone) {
        this.loaderService.hide();
        this.initialLoadDone = true;
      }
    });
  }

  rangeValues(_event: DateRangePickerValue) {
    // Kept for parity with monthly-report-1 date picker wiring
  }

  /**
   * KPI card delta suffix, rendered AFTER the value (e.g. "+20.0% MoM", "-99.16% vs Previous
   * Period") — reflects the selected filter's actual granularity/type ("MoM" only for a
   * calendar-month selection, "YoY" only for a calendar-year selection) instead of a hardcoded
   * "MoM". See getComparisonLabel() for the full derivation.
   */
  get comparisonLabel(): KpiComparisonLabel {
    return getComparisonLabel(this.comparisonType, this.selectedPresetLabel, this.start_date, this.end_date);
  }

  /**
   * API-facing counterpart of comparisonLabel: only YoY ("previous-year") maps to
   * "prevYear" — every other comparison mode (previous-period, previous-month, or no explicit
   * selection, which still defaults to an MoM-style window) maps to "prevPeriod". Sent as
   * `compare_to` on every KPI request so the backend knows which window prev_start_time/
   * prev_end_time represent.
   */
  private get compareTo(): 'prevPeriod' | 'prevYear' {
    return this.comparisonType === 'previous-year' ? 'prevYear' : 'prevPeriod';
  }

  stageTrendLabel(momGrowthPct: number | null | undefined): string {
    const value = Number(momGrowthPct) || 0;
    if (value > 0) return 'growing';
    if (value < 0) return 'shrinking';
    return 'unchanged';
  }
  private normalizeDate(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  /**
   * The comparison window used as the MoM baseline whenever the date-picker's own
   * comparison toggle wasn't set — calendar-equivalent (whole year(s)/month(s)) when the
   * selected range is calendar-aligned (e.g. "Last Year"'s Jan 1 - Dec 31), otherwise the
   * immediately preceding period of the same length.
   * e.g. start=2026-08-25 end=2026-08-31 (7 days) -> prev 2026-08-18..2026-08-24.
   */
  private computePreviousPeriod(start: string, end: string): { prevStart: string; prevEnd: string } {
    const startDay = dayjs.tz(start, LA_TIMEZONE);
    const endDay = dayjs.tz(end, LA_TIMEZONE);
    const { start: prevStart, end: prevEnd } = getCalendarEquivalentPreviousPeriod(startDay, endDay);
    return {
      prevStart: prevStart.format('YYYY-MM-DD'),
      prevEnd: prevEnd.format('YYYY-MM-DD')
    };
  }

  /**
   * Same MoM-derivation pattern as the (working) Total Contracts card: prefer the API's own
   * growth field when it's actually present, and only fall back to computing
   * ((current - previousCount) / previousCount) * 100 when the API didn't send a growth value.
   * A real API 0 is respected — only a missing field triggers the fallback.
   */
  private resolveMomGrowth(current: number, obj: any, growthField: string = 'mom_growth_pct'): number {
    if (obj?.[growthField] !== undefined && obj?.[growthField] !== null) {
      return Number(obj[growthField]);
    }
    if (obj?.growth !== undefined && obj?.growth !== null) {
      return Number(obj.growth);
    }
    const prevCount = Number(obj?.previousCount) || 0;
    return prevCount ? ((current - prevCount) / prevCount) * 100 : 0;
  }

  /** Slugifies a display name (e.g. "Demand Letter" -> "demand_letter") for template ngClass matching. */
  private deriveKey(name: string | undefined | null): string {
    return (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  /** Formats a dollar amount as compact currency (e.g. 4053000 -> "$4.05M"), matching the card's previous static copy style. */
  private formatCompactCurrency(amount: number): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(Number(amount) || 0);
    } catch {
      return '$' + (Number(amount) || 0).toLocaleString();
    }
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    console.log('[recuvery-dashboard] onDateRangeChange() event =', event);
    const rangeKey = [
      event.start_date,
      event.end_date,
      event.comparison_type,
      event.previous_start_date ?? '',
      event.previous_end_date ?? '',
      event.unit
    ].join('|');

    if (rangeKey === this.lastDateRangeKey) return;
    this.lastDateRangeKey = rangeKey;

    this.start_date = this.normalizeDate(event.start_date);
    this.end_date = this.normalizeDate(event.end_date);
    this.comparisonType = event.comparison_type;
    this.selectedPresetLabel = event.preset_label || '';

    if (event.comparison_type !== 'none' && event.previous_start_date && event.previous_end_date) {
      // User explicitly picked a comparison window (e.g. "same period last year") — honor it.
      this.prev_start_date = this.normalizeDate(event.previous_start_date);
      this.prev_end_date = this.normalizeDate(event.previous_end_date);
    } else {
      // No explicit comparison chosen — MoM still needs a previous-period window, so default
      // to the immediately preceding period of the same length as the selected range.
      const { prevStart, prevEnd } = this.computePreviousPeriod(this.start_date, this.end_date);
      this.prev_start_date = prevStart;
      this.prev_end_date = prevEnd;
    }

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date === this.end_date) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit || this.unitService.getUnitSuggestions(this.start_date, this.end_date).recommended as any);
    this.datesReady = true;
    this.skipNextEffectFetch = true;
    console.log('[recuvery-dashboard] resolved comparisonType =', this.comparisonType, 'prev_start_date =', this.prev_start_date, 'prev_end_date =', this.prev_end_date);
    this.fetchData();
  }

  private fetchData() {
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.start_date || !this.end_date) return;

    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );

    const options = {
      showErrorToaster: false,
      showSuccessToaster: false,
      showLoader: true,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    // Endpoints below are relative: ApiService resolves the host from the
    // productServerName entry in environment.productServerUrls (recuvery →
    // testexternal.recuvery.com/external/prism on test, external.recuvery.com on live),
    // so no environment-specific URL is spelled out in this component.
    const toolsPath = productConfig.reportToolsPath || 'visionboard';

    const payload: any = {
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit()
    };

    if (this.prev_start_date && this.prev_end_date) {
      payload.prev_start_time = this.prev_start_date;
      payload.prev_end_time = this.prev_end_date;
      payload.compare_start_time = this.prev_start_date;
      payload.compare_end_time = this.prev_end_date;
      // Every KPI request shares this one payload object, so compare_to is applied
      // consistently to Total Debt Recovered, Paid Off, Active Portfolio, Total Contracts,
      // Recovery Rate, and every other endpoint below without repeating it per-call.
      payload.compare_to = this.compareTo;
    }

    const requests: any = {
      sourceConcentration: this.apiService.post(`${toolsPath}/decision_source_concentration`, payload, options).pipe(catchError(() => of(null))),
      recoveryActivation: this.apiService.post(`${toolsPath}/decision_recovery_activation`, payload, options).pipe(catchError(() => of(null))),
      stageThroughput: this.apiService.post(`${toolsPath}/decision_stage_throughput`, payload, options).pipe(catchError(() => of(null))),
      debtRecovered: this.apiService.post(`${toolsPath}/total_debt_recovered`, payload, options).pipe(catchError(() => of(null))),
      accountsPaidOff: this.apiService.post(`${toolsPath}/accounts_paid_off`, payload, options).pipe(catchError(() => of(null))),
      activePortfolio: this.apiService.post(`${toolsPath}/active_portfolio_stats`, payload, options).pipe(catchError(() => of(null))),
      totalContracts: this.apiService.post(`${toolsPath}/contract_types_count`, payload, options).pipe(catchError(() => of(null))),
      recoveryRate: this.apiService.post(`${toolsPath}/recovery_rate`, payload, options).pipe(catchError(() => of(null))),
      recoveryJourney: this.apiService.post(`${toolsPath}/recovery_journey_stages`, payload, options).pipe(catchError(() => of(null))),
      contractLifecycle: this.apiService.post(`${toolsPath}/contract_lifecycle_status`, payload, options).pipe(catchError(() => of(null))),
      recoverySource: this.apiService.post(`${toolsPath}/recovery_by_source`, payload, options).pipe(catchError(() => of(null))),
      platformFee: this.apiService.post(`${toolsPath}/platform_fee_revenue`, payload, options).pipe(catchError(() => of(null))),
      channelFunnel: this.apiService.post(`${toolsPath}/channel_funnel_stats`, payload, options).pipe(catchError(() => of(null)))
    };

    this.isLoading.set(true);
    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = forkJoin(requests).subscribe({
      next: (results: any) => {
        const res1 = results.sourceConcentration;
        const res2 = results.recoveryActivation;
        const res3 = results.stageThroughput;

        // Process Source Concentration
        let d1 = res1?.data || res1 || {};
        if (d1 && (d1.direct || d1.referred)) {
          const direct = d1.direct || {};
          const referred = d1.referred || {};
          this.decisionSource.set({
            source_name: direct.source_name || 'Recuvery-direct',
            account_count: Number(direct.account_count) || 0,
            pct_of_book: Number(direct.pct_of_book) || 0,
            referred_pct_of_book: Number(referred.pct_of_book) || 0
          });
        }

        // Process Recovery Activation
        let d2 = res2?.data || res2 || {};
        if (Array.isArray(d2)) d2 = d2[0] || {};
        const contacted = Number(d2.contacted_count) || 0;
        const paid = Number(d2.paid_off_count) || 0;
        const rate = contacted > 0 ? (paid / contacted) * 100 : 0;
        this.decisionRecovery.set({
          contacted_count: contacted,
          paid_off_count: paid,
          in_progress_count: Number(d2.in_progress_count) || 0,
          contact_to_recovery_rate: d2.contact_to_recovery_rate || rate,
          stage_name: d2.stage_name || d2.channel_name || d2.segment_name || 'Notification'
        });

        // Process Pipeline Throughput
        const throughputStages: any[] = Array.isArray(res3?.data?.stages) ? res3.data.stages : [];
        const findStage = (name: string) =>
          throughputStages.find((s: any) => (s?.stage_name || '').toLowerCase() === name.toLowerCase());
        const notification = findStage('notification') || {};
        const collection = findStage('collection') || {};
        this.decisionPipeline.set({
          stage_name: 'Notification',
          mom_growth_pct: Number(notification.mom_growth_pct) || 0,
          next_stage_name: 'Collection',
          next_mom_growth_pct: Number(collection.mom_growth_pct) || 0
        });

        // Process Debt Recovered
        let d4 = results.debtRecovered?.data || results.debtRecovered || {};
        if (Array.isArray(d4)) d4 = d4[0] || {};
        const totalRecoveredAmt = Number(d4.total_recovered_amt) || 0;
        this.debtRecovered.set({
          total_recovered_amt: totalRecoveredAmt,
          by_source: Array.isArray(d4.by_source) ? d4.by_source : [],
          mom_growth_pct: this.resolveMomGrowth(totalRecoveredAmt, d4)
        });

        // Process Accounts Paid Off
        let d5 = results.accountsPaidOff?.data || results.accountsPaidOff || {};
        if (Array.isArray(d5)) d5 = d5[0] || {};
        const paidOffCount = Number(d5.paid_off_count) || 0;
        this.accountsPaidOff.set({
          paid_off_count: paidOffCount,
          settled_count: Number(d5.settled_count) || 0,
          cancelled_count: Number(d5.cancelled_count) || 0,
          book_total: Number(d5.book_total) || 0,
          mom_growth_pct: this.resolveMomGrowth(paidOffCount, d5)
        });

        // Process Active Portfolio
        let d6 = results.activePortfolio?.data || results.activePortfolio || {};
        if (Array.isArray(d6)) d6 = d6[0] || {};
        const activeTotal = Number(d6.active_total) || 0;
        this.activePortfolio.set({
          active_total: activeTotal,
          by_source: Array.isArray(d6.by_source) ? d6.by_source : [],
          mom_growth_pct: this.resolveMomGrowth(activeTotal, d6)
        });

        // Process Total Contracts
        // contract_types_count carries the same by-source breakdown shape (source_name, count)
        // the other "book stands" cards already read (see Debt Recovered / Active Portfolio's
        // by_source handling above) — mirror that pattern here instead of reading a single
        // contract-type record out of unit_data/overall, which measures a different axis
        // (contract type, e.g. "Debt Recovery Payment Plan") and isn't what this card displays.
        let d8 = results.totalContracts?.data || results.totalContracts || {};
        if (Array.isArray(d8)) d8 = d8[0] || {};
        const totalContractsBySource = (Array.isArray(d8.by_source) ? d8.by_source : []).map((s: any) => ({
          source_name: s.source_name,
          source_key: s.source_key || this.deriveKey(s.source_name),
          count: Number(s.count) || 0
        }));

        this.totalContracts.set({
          total_contracts: Number(d8.total_contracts) || 0,
          business_count: Number(d8.total_businesses) || 0,
          by_source: totalContractsBySource,
          mom_growth_pct: this.resolveMomGrowth(Number(d8.total_contracts) || 0, d8)
        });

        // Process Recovery Rate
        let d7 = results.recoveryRate?.data || results.recoveryRate || {};
        if (Array.isArray(d7)) d7 = d7[0] || {};
        const recoveryRate = (Number(d7.paid_count) || 0) / (Number(d7.book_total) || 1) * 100;
        const recoveryRatePct = d7.recovery_rate_pct !== undefined ? Number(d7.recovery_rate_pct) : recoveryRate;
        this.recoveryRate.set({
          recovery_rate_pct: recoveryRatePct,
          paid_count: Number(d7.paid_count) || 0,
          book_total: Number(d7.book_total) || 0,
          mom_pp_change: this.resolveMomGrowth(recoveryRatePct, d7, 'mom_pp_change')
        });

        // Process Recovery Journey
        let rj = results.recoveryJourney?.data || results.recoveryJourney || {};
        if (Array.isArray(rj)) rj = rj[0] || {};
        let stages = Array.isArray(rj.stages) ? rj.stages : [];


        // Ensure stage_breakdown is an array
        const breakdown = Array.isArray(rj.stage_breakdown) ? rj.stage_breakdown : [];
        const disputes_new = breakdown.filter((item: any) => item.type === 'dispute');
        const aggregatedDispute = {
          key: 'dispute',
          name: 'Disputes Total',
          count: disputes_new.reduce((sum: number, d: any) => sum + (Number(d.count) || 0), 0),
          debt_amount: disputes_new.reduce((sum: number, d: any) => sum + (Number(d.debt_amount) || 0), 0),
          pct_of_book: disputes_new.reduce((sum: number, d: any) => sum + (Number(d.pct_of_book) || 0), 0),
          days_in_stage: Math.max(...disputes_new.map((d: any) => Number(d.days_in_stage) || 0), 0)
        };
        const combinedJourney = [...stages, aggregatedDispute];

        // Needs-management-attention: derive the flagged stage/metrics from the live stages[]
        // instead of the previously hardcoded "Notification/$4.05M/45.1%/41%/Collection/52 days" copy.
        const stageWithMaxDebt = stages.length
          ? stages.reduce((max: any, s: any) => (Number(s.debt_amount) || 0) > (Number(max?.debt_amount) || 0) ? s : max, stages[0])
          : null;
        const advanceCandidates = stages.filter((s: any) => s.advance_rate_pct !== null && s.advance_rate_pct !== undefined);
        const stageWithLowestAdvance = advanceCandidates.length
          ? advanceCandidates.reduce((min: any, s: any) => (Number(s.advance_rate_pct) || 0) < (Number(min.advance_rate_pct) || 0) ? s : min, advanceCandidates[0])
          : null;
        const stageWithLongestCycle = stages.length
          ? stages.reduce((max: any, s: any) => (Number(s.days_in_stage) || 0) > (Number(max?.days_in_stage) || 0) ? s : max, stages[0])
          : null;

        // Per-stage caption keyword, derived purely from this range's own metrics (not the
        // stage's name/key) so it stays accurate as the leading debt/bottleneck stage shifts
        // between date ranges:
        //  - a stage with no advance_rate_pct has nothing further to advance to -> terminal/"recovered"
        //  - the stage carrying the most debt -> "largest exposure"
        //  - among the remaining stages that still have an advance rate, the slowest one -> "bottleneck"
        //  - the very first stage in the journey (accounts that haven't had a notice/demand
        //    letter sent yet) -> "awaiting notice" — only true for that earliest stage, since by
        //    the time an account reaches a later stage (e.g. Collection) notice has already gone out
        //  - any other stage not otherwise flagged -> "in progress" (neutral, no false claim about notice status)
        const stagesWithKeyword = stages.map((s: any, index: number) => {
          let keyword = index === 0 ? 'awaiting notice' : 'in progress';
          if (s.advance_rate_pct === null || s.advance_rate_pct === undefined) {
            keyword = 'recovered';
          } else if (stageWithMaxDebt && s === stageWithMaxDebt) {
            keyword = 'largest exposure';
          } else if (stageWithLowestAdvance && s === stageWithLowestAdvance) {
            keyword = 'bottleneck';
          }
          return { ...s, keyword };
        });

        this.recoveryJourney.set({
          stages: stagesWithKeyword,
          stageBreakdown: combinedJourney,
          dispute_count: Number(rj.dispute_count) || 0,
          dispute_pct: Number(rj.dispute_pct) || 0,
          total_accounts: Number(rj.total_accounts) || 0,
          total_debt_amount: Number(rj.total_debt_amount) || 0,
          has_attention_data: stages.length > 0,
          attention_stage_name: stageWithMaxDebt?.name || '',
          attention_amount_compact: this.formatCompactCurrency(stageWithMaxDebt?.debt_amount),
          attention_pct_of_book: Number(stageWithMaxDebt?.pct_of_book) || 0,
          attention_lowest_advance_pct: Number(stageWithLowestAdvance?.advance_rate_pct) || 0,
          attention_longest_stage_name: stageWithLongestCycle?.name || '',
          attention_longest_days: Number(stageWithLongestCycle?.days_in_stage) || 0
        });

        // Process Contract Lifecycle
        let cl = results.contractLifecycle?.data || results.contractLifecycle || {};
        if (Array.isArray(cl)) cl = cl[0] || {};
        this.contractLifecycle.set({
          statuses: Array.isArray(cl.statuses) ? cl.statuses : [],
          total_contracts: Number(cl.total_contracts) || 0
        });

        // Process Recovery Source
        let rs = results.recoverySource?.data || results.recoverySource || {};
        if (Array.isArray(rs)) rs = rs[0] || {};
        const recoverySources = (Array.isArray(rs.sources) ? rs.sources : []).map((s: any) => {
          const accounts = Number(s.accounts) || 0;
          const paid = Number(s.paid) || 0;
          return {
            source_name: s.name,
            source_key: s.source_key || this.deriveKey(s.name),
            accounts,
            contacts: Number(s.contacts) || 0,
            paid,
            recovered_amt: Number(s.recovered_amt) || 0,
            // API doesn't send a rate directly — derive it per the card's own documented formula
            // (paid-off accounts ÷ accounts from that source) unless the API already supplies one.
            recovery_rate_pct: s.recovery_rate_pct !== undefined ? Number(s.recovery_rate_pct) : (accounts > 0 ? (paid / accounts) * 100 : 0)
          };
        });
        this.recoverySource.set({
          sources: recoverySources,
          referred_summary: rs.referred_summary || {},
          direct_summary: rs.direct_summary || {}
        });

        // Process Platform Fee
        let pf = results.platformFee?.data || results.platformFee || {};
        if (Array.isArray(pf)) pf = pf[0] || {};
        const platformFeeBySource = (Array.isArray(pf.by_source) ? pf.by_source : []).map((s: any) => ({
          source_name: s.source,
          source_key: s.source_key || this.deriveKey(s.source),
          total: Number(s.total) || 0,
          contract_fees: Number(s.contract_fees) || 0,
          payoff_fees: Number(s.payoff_fees) || 0,
          pct_of_total: Number(s.pct_of_total) || 0
        }));
        this.platformFee.set({
          total_revenue: Number(pf.total_revenue) || 0,
          by_source: platformFeeBySource
        });

        // Process Channel Funnel
        let cf = results.channelFunnel?.data || results.channelFunnel || {};
        if (Array.isArray(cf)) cf = cf[0] || {};
        let channels = (Array.isArray(cf.channels) ? cf.channels : []).map((c: any) => {
          const sent = Number(c.sent) || 0;
          const opened = Number(c.opened) || 0;
          const paid = Number(c.paid) || 0;
          const open_rate_pct = c.open_rate_pct !== undefined && c.open_rate_pct !== null
            ? Number(c.open_rate_pct)
            : (sent > 0 ? (opened / sent) * 100 : 0);
          const end_yield_pct = c.end_yield_pct !== undefined && c.end_yield_pct !== null
            ? Number(c.end_yield_pct)
            : (sent > 0 ? (paid / sent) * 100 : 0);
          return {
            name: c.name,
            channel_key: c.channel_key || this.deriveKey(c.name),
            sent,
            opened,
            attempted: Number(c.attempted) || 0,
            paid,
            delivery_failures: Number(c.delivery_failures) || 0,
            open_rate_pct,
            end_yield_pct,
            // The API already computes rank (null for channels like 'calls' that don't
            // funnel through payment) — trust it instead of recalculating locally.
            rank: c.rank !== undefined ? c.rank : null
          };
        });
        // Display order follows the API's own rank (best first); unranked channels sort last.
        channels.sort((a: any, b: any) => {
          const rankA = a.rank === null || a.rank === undefined ? Infinity : a.rank;
          const rankB = b.rank === null || b.rank === undefined ? Infinity : b.rank;
          return rankA - rankB;
        });
        this.channelFunnel.set({
          channels: channels
        });
        let demandLeter = this.channelFunnel()?.channels.find((c: any) => c.channel_key === 'demand_letter');
        let sms = this.channelFunnel()?.channels.find((c: any) => c.channel_key === 'sms');
        let email = this.channelFunnel()?.channels.find((c: any) => c.channel_key === 'email');
        this.demandLeter.set(demandLeter);
        this.sms.set(sms);
        this.email.set(email);
        // Every KPI signal above is now populated together — only now is it safe to drop the
        // skeleton overlays, so no card ever briefly shows a stale/partial/zero value.
        this.isLoading.set(false);
      },
      error: () => {
        // Handle error gracefully if needed
        this.isLoading.set(false);
      }
    });
  }
  timeToContractTotal = computed(() => {
    return this.contractLifecycle()?.total_contracts || 7581;
  });

  timeToContractDonut = computed(() => {
    const statuses = this.contractLifecycle()?.statuses || [];

    // Map colors to possible status names based on previous hardcoded UI
    const colorMap: any = {
      'Active': '#7c3aed',
      'Overdue': '#dc2626',
      'Completed': '#16a34a',
      'Compeleted': '#16a34a',
      'Cancelled': '#6a7c93'
    };

    if (statuses.length > 0) {
      return statuses.map((status: any) => ({
        label: status.name || 'Unknown',
        value: Number(status.pct_of_total) || 0,
        count: Number(status.count) || 0,
        color: colorMap[status.name] || '#7c3aed'
      }));
    }

    // Default fallback from UI
    return [
      { label: 'Active', value: 55.4, count: 4200, color: '#7c3aed' },
      { label: 'Overdue', value: 8.0, count: 606, color: '#dc2626' },
      { label: 'Completed', value: 31.7, count: 2403, color: '#16a34a' },
      { label: 'Cancelled', value: 4.9, count: 371, color: '#6a7c93' }
    ];
  });
}
